import * as React from "react";
import { TabNode } from "../model/TabNode";
import { TabSetNode } from "../model/TabSetNode";
import { CLASSES } from "../Types";
import { LayoutInternal } from "./Layout";
import { BorderNode } from "../model/BorderNode";
import { Actions } from "../model/Actions";

/** @internal */
export interface ITabProps {
    layout: LayoutInternal;
    node: TabNode;
    selected: boolean;
    path: string;
}

/** @internal */
export const Tab = (props: ITabProps) => {
    const { layout, selected, node, path } = props;
    const selfRef = React.useRef<HTMLDivElement | null>(null);
    const firstSelect = React.useRef<boolean>(true);

    const parentNode = node.getParent() as TabSetNode | BorderNode;
    const rect = parentNode.getContentRect()!;

    // A tab body is absolutely positioned over its tabset rather than nested
    // inside it, so hovering the body gives the tabset no :hover to style with.
    // Marking the tabset from here lets toolbar chrome (fold, maximize) reveal
    // itself for the whole panel; a class toggle keeps it off the render path.
    const setTabSetHovered = (hovered: boolean) => {
        const parent = node.getParent();
        if (!(parent instanceof TabSetNode)) {
            return;
        }
        // found by id: layout paths are only unique within a row, so tabsets in
        // different rows can share one (both are their own row's "/ts0")
        const tabSetElement = layout.getRootDiv()?.querySelector(`.${CLASSES.FLEXLAYOUT__TABSET_CONTAINER}[data-tabset-id="${CSS.escape(parent.getId())}"]`);
        tabSetElement?.classList.toggle(CLASSES.FLEXLAYOUT__TABSET_HOVER, hovered);
    };

    const onPointerEnter = () => setTabSetHovered(true);
    const onPointerLeave = () => setTabSetHovered(false);

    React.useLayoutEffect(() => {
        const element = node.getMoveableElement()!;
        selfRef.current!.appendChild(element);
        node.setMoveableElement(element);

        const handleScroll = () => {
            node.saveScrollPosition();
        };

        // keep scroll position
        element.addEventListener('scroll', handleScroll);

        // listen for clicks to change active tabset
        selfRef.current!.addEventListener("pointerdown", onPointerDown);

        // native listeners, not react props: the tab's content is a moveable element
        // appended into this div, but it sits elsewhere in the react tree, so react's
        // synthetic events from inside the pane never reach handlers declared here
        // (same reason pointerdown above is native)
        selfRef.current!.addEventListener("pointerenter", onPointerEnter);
        selfRef.current!.addEventListener("pointerleave", onPointerLeave);

        return () => {
            element.removeEventListener('scroll', handleScroll);
            if (selfRef.current) {
                selfRef.current.removeEventListener("pointerdown", onPointerDown);
                selfRef.current.removeEventListener("pointerenter", onPointerEnter);
                selfRef.current.removeEventListener("pointerleave", onPointerLeave);
            }
            setTabSetHovered(false); // a hidden/unmounted tab must not leave its tabset stuck hovered
            node.setVisible(false);
        }
    }, []);

    React.useEffect(() => {
        if (node.isSelected()) {
            if (firstSelect.current) {
                node.restoreScrollPosition(); // if window docked back in
                firstSelect.current = false;
            }
        }
    });

    const onPointerDown = () => {
        const parent = node.getParent()!; // cannot use parentNode here since will be out of date
        if (parent instanceof TabSetNode) {
            if (!parent.isActive()) {
                layout.doAction(Actions.setActiveTabset(parent.getId(), layout.getWindowId()));
            }
        }
    };

    node.setRect(rect); // needed for resize event
    const cm = layout.getClassName;
    const style: Record<string, any> = {};

    rect.styleWithPosition(style);

    let overlay = null;

    if (selected) {
        node.setVisible(true);
        if (document.hidden && node.isEnablePopoutOverlay()) {
            const overlayStyle: Record<string, any> = {};
            rect.styleWithPosition(overlayStyle);
            overlay = (<div style={overlayStyle} className={cm(CLASSES.FLEXLAYOUT__TAB_OVERLAY)}></div>)
        }
    } else {
        style.display = "none";
        node.setVisible(false);
    }

    if (parentNode instanceof TabSetNode) {
        if (node.getModel().getMaximizedTabset(layout.getWindowId()) !== undefined) {
            if (parentNode.isMaximized()) {
                style.zIndex = 10;
            } else {
                style.display = "none";
            }
        }
        // a folded tabset shows only its strip; its content panes (absolutely
        // positioned siblings) are hidden the same way maximize hides others
        if (parentNode.isFolded()) {
            style.display = "none";
        }
    }

    if (parentNode instanceof BorderNode) {
        if (!parentNode.isShowing()) {
            style.display = "none";
        }
    }

    // hidden without a pointerleave (folded, or maximized away under the cursor)
    const hidden = style.display === "none";
    React.useEffect(() => {
        if (hidden) {
            setTabSetHovered(false);
        }
    }, [hidden]);

    let className = cm(CLASSES.FLEXLAYOUT__TAB);
    if (parentNode instanceof BorderNode) {
        className += " " + cm(CLASSES.FLEXLAYOUT__TAB_BORDER);
        className += " " + cm(CLASSES.FLEXLAYOUT__TAB_BORDER_ + parentNode.getLocation().getName());
    }

    if (node.getContentClassName() !== undefined) {
        className += " " + node.getContentClassName();
    }

    return (
        <>
            {overlay}

            <div
                ref={selfRef}
                style={style}
                className={className}
                data-layout-path={path}
            />
        </>
    );
};


